import { Button, Checkbox } from '@shared/ui/kit';
import { UnstyledButton } from '@shared/ui/unstyled-button';
import { fonts } from '@shared/tokens/fonts.stylex';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import {
  FILTER_DROPDOWN_SIZES,
  FILTER_FIELD_INVITATION,
  getFilterDropdownStyle,
  INVITATION_FILTER_STATUS_ITEMS,
} from '../consts';
import styles from '../FilterDropdown/FilterDropdown.module.scss';

const invitationFooterButtonSettings = {
  fontSize: '14px',
  fontWeight: '400',
  fontFamily: fonts.din2014Regular,
};

interface InvitationFilterDropdownProps {
  selectedStatuses: string[];
  onApply: (value: string[]) => void;
  onClose: () => void;
  options?: { label: string; value: string }[];
  filterField?: string;
}

export const InvitationFilterDropdown = ({
  selectedStatuses: initialSelectedStatuses,
  onApply,
  onClose,
  options = INVITATION_FILTER_STATUS_ITEMS,
  filterField = FILTER_FIELD_INVITATION,
}: InvitationFilterDropdownProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(initialSelectedStatuses);
  const statusValues = options.map(({ value }) => value);
  const isAllStatusesSelected = selectedStatuses.length === options.length;
  const isSomeStatusesSelected = selectedStatuses.length > 0 && !isAllStatusesSelected;

  useEffect(() => {
    const onDocumentMouseDown = (event: MouseEvent) => {
      if (!dropdownRef.current) return;
      if (dropdownRef.current.contains(event.target as Node)) return;
      onClose();
    };

    document.addEventListener('mousedown', onDocumentMouseDown);
    return () => {
      document.removeEventListener('mousedown', onDocumentMouseDown);
    };
  }, [onClose]);

  useEffect(() => {
    setSelectedStatuses(initialSelectedStatuses);
  }, [initialSelectedStatuses]);

  const onToggleAllStatuses = () => {
    if (isAllStatusesSelected) {
      setSelectedStatuses([]);
      return;
    }

    setSelectedStatuses(statusValues);
  };

  const onToggleStatus = (item: string) => {
    if (selectedStatuses.includes(item)) {
      setSelectedStatuses((prev) => prev.filter((status) => status !== item));
      return;
    }

    setSelectedStatuses((prev) => [...prev, item]);
  };

  const onApplyFilter = () => {
    onApply(selectedStatuses);
  };

  return (
    <div
      className={clsx(styles.filterDropdown, styles.filterDropdownInvitation)}
      style={getFilterDropdownStyle(FILTER_DROPDOWN_SIZES[filterField as keyof typeof FILTER_DROPDOWN_SIZES])}
      ref={dropdownRef}
    >
      <div className={styles.filterDropdownContent}>
        <div className={styles.filterOptions}>
          <UnstyledButton className={styles.filterOption} onClick={onToggleAllStatuses}>
            <Checkbox
              size="small"
              isChecked={isAllStatusesSelected}
              isIndeterminate={isSomeStatusesSelected}
              onChange={() => {}}
            />
            <span className={styles.filterOptionText}>Все статусы</span>
          </UnstyledButton>

          <div className={styles.filterOptionsDivider} />

          <div className={styles.filterProjectList}>
            {options.map((item) => (
              <UnstyledButton
                key={item.value}
                className={styles.filterOption}
                onClick={() => onToggleStatus(item.value)}
              >
                <Checkbox size="small" isChecked={selectedStatuses.includes(item.value)} onChange={() => {}} />
                <span className={styles.filterOptionText}>{item.label}</span>
              </UnstyledButton>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.filterDropdownFooter}>
        <div className={styles.filterFooterApply}>
          <Button variant="accent" size="small" onClick={onApplyFilter} settings={invitationFooterButtonSettings}>
            Применить
          </Button>
        </div>
        <div className={styles.filterFooterClose}>
          <Button variant="link" size="small" onClick={onClose} settings={invitationFooterButtonSettings}>
            Закрыть
          </Button>
        </div>
      </div>
    </div>
  );
};
